/**
 * Droits de l'utilisateur connecte, lus dans la matrice partagee
 * (`@leads/shared`, conception 3.1).
 *
 * L'interface ne fait que masquer ce que l'API refuserait : la verification
 * qui fait foi reste celle du serveur. Un role se lit toujours a travers ses
 * permissions, jamais par comparaison directe (`role === 'ADMIN'`).
 */
import { useMemo } from 'react';
import { aPermission } from '@leads/shared';
import type { LeadResumeDTO, Permission, Role, UtilisateurDTO } from '@leads/shared';
import { useAuth } from './auth';

type SuiviDuLead = Pick<LeadResumeDTO, 'suivi'>;

/** Une permission, ou une liste dont une seule suffit. */
export type Exigence = Permission | readonly Permission[];

/** `undefined` n'exige rien ; sans role (pas de session), rien n'est permis. */
export function satisfait(role: Role | null | undefined, exigence: Exigence | undefined): boolean {
  if (exigence === undefined) return true;
  if (!role) return false;
  const permissions: readonly Permission[] = typeof exigence === 'string' ? [exigence] : exigence;
  return permissions.some((permission) => aPermission(role, permission));
}

/**
 * Proprietaire d'un lead, `null` pour le vivier (suivi absent compris). Seul
 * endroit qui lit `suivi.proprietaireId` : si le DTO change, on n'adapte qu'ici.
 */
export function proprietaireDuLead(lead: Pick<LeadResumeDTO, 'suivi'>): string | null {
  return lead.suivi?.proprietaireId ?? null;
}

export function estDansVivier(lead: Pick<LeadResumeDTO, 'suivi'>): boolean {
  return proprietaireDuLead(lead) === null;
}

/**
 * Meme regle que l'API (conception 3.2) : tout lead avec `leads.modifier_tous`,
 * sinon, avec `leads.ecrire`, ses propres leads et ceux du vivier.
 */
export function peutModifierLead(
  utilisateur: Pick<UtilisateurDTO, 'id' | 'role'> | null,
  lead: Pick<LeadResumeDTO, 'suivi'>,
): boolean {
  if (!utilisateur) return false;
  if (aPermission(utilisateur.role, 'leads.modifier_tous')) return true;
  const proprietaire = proprietaireDuLead(lead);
  return aPermission(utilisateur.role, 'leads.ecrire') && (proprietaire === null || proprietaire === utilisateur.id);
}

/** Dit la prise automatique du vivier a l'auteur de l'ecriture (`usePriseDuVivier`). */
export const MESSAGE_LEAD_ATTRIBUE = 'Ce lead vous est désormais attribué.';

/**
 * Meme regle que l'API (conception 3.2) : ecrire sur un lead du vivier
 * l'attribue a son auteur, s'il ne modifie pas tout. `apres` : le lead renvoye
 * par l'ecriture, qui fait foi (une ecriture qui ne change rien ne prend pas le
 * lead). Sans lui (echange, contact : l'API ne renvoie pas le lead, mais ecrit
 * toujours), la regle decide.
 */
export function aPrisLeVivier(
  utilisateur: Pick<UtilisateurDTO, 'id' | 'role'> | null,
  avant: SuiviDuLead,
  apres?: SuiviDuLead,
): boolean {
  if (!utilisateur || !estDansVivier(avant)) return false;
  if (apres) return proprietaireDuLead(apres) === utilisateur.id;
  return aPermission(utilisateur.role, 'leads.ecrire') && !aPermission(utilisateur.role, 'leads.modifier_tous');
}

/**
 * Prise automatique du vivier, dite par l'ecriture qui l'entraine : sans cela,
 * seul l'en-tete de la fiche change, sans annonce. Aide unique des toasts de
 * succes des ecritures sur un lead.
 */
export function usePriseDuVivier() {
  const { utilisateur } = useAuth();
  return useMemo(
    () => ({
      aPris: (avant: SuiviDuLead, apres?: SuiviDuLead): boolean => aPrisLeVivier(utilisateur, avant, apres),
      /** `message`, suivi de `MESSAGE_LEAD_ATTRIBUE` quand l'ecriture a pris le lead. */
      message: (message: string, avant: SuiviDuLead, apres?: SuiviDuLead): string =>
        aPrisLeVivier(utilisateur, avant, apres) ? `${message} ${MESSAGE_LEAD_ATTRIBUE}` : message,
    }),
    [utilisateur],
  );
}

export interface Permissions {
  role: Role | null;
  peut: (exigence: Exigence) => boolean;
  peutModifierLead: (lead: Pick<LeadResumeDTO, 'suivi'>) => boolean;
}

export function usePermissions(): Permissions {
  const { utilisateur } = useAuth();
  return useMemo<Permissions>(
    () => ({
      role: utilisateur?.role ?? null,
      peut: (exigence) => satisfait(utilisateur?.role, exigence),
      peutModifierLead: (lead) => peutModifierLead(utilisateur, lead),
    }),
    [utilisateur],
  );
}
